import { FetchResult, MutationFunctionOptions } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { zodResolver } from '@hookform/resolvers/zod';
import { navigate } from 'gatsby';
import { cond, equals, filter, includes, keys, map } from 'ramda';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import fullWidth from '@ui/styled/FullWidth';

import { ICancelationReason } from '@hooks/useMemberInfo';

import { checkbox, optionalString } from '@utils/validation';
import { convertKeysToSchema } from '@utils/zod';

import { ICancelationSurveyCommonProps } from './types';
import AdditionalInfoView from './views/AdditionalInfoView.component';
import SurveyView from './views/SurveyView.component';

export interface ICancelationSurveyProps extends ICancelationSurveyCommonProps {
	cancelationReasonData: ICancelationReason[];
	cancelationReasonMutation: (options?: MutationFunctionOptions) => Promise<FetchResult<string>>;
}

const FullWidthForm = fullWidth('form');

const CancelationSurvey: FC<ICancelationSurveyProps> = ({
	cancelationReasonData,
	cancelationReasonMutation,
	cancelationReasons,
	pageContent,
}) => {
	const [view, setView] = useState<'cancelation_survey' | 'additional_info'>('cancelation_survey');

	const convertedSchema = convertKeysToSchema(cancelationReasons, checkbox, {
		feedback: optionalString,
	});
	const cancelationSchema = z.object(convertedSchema);

	type Schema = Partial<z.input<typeof cancelationSchema>>;

	const form = useForm<Schema>({
		defaultValues: {},
		resolver: zodResolver(cancelationSchema),
		mode: 'onChange',
	});
	const { control, handleSubmit, register, setValue, watch, getValues } = form;
	watch(cancelationReasons); // Used to passively force rerenders for CancelationCheckboxes in SurveyView

	const onSubmit = async (data: Record<string, unknown>) => {
		console.log(data);
		const feedback = data?.feedback;

		const filteredReasons: Record<string, true> = filter(
			(reasonName?: boolean | string) => reasonName === true,
			data
		);
		const reasonData = filter((reason: ICancelationReason) => {
			return includes(reason.butterKey, keys(filteredReasons));
		}, cancelationReasonData);
		const reasons = map<ICancelationReason, string>((reason) => {
			return reason.cancellationReasonId;
		}, reasonData);
		await cancelationReasonMutation({
			variables: {
				feedback,
				reasons,
			},
		}).catch((e) => {
			// Eat the error, there's no reason to prevent members from moving on if the mutation fails
			datadogRum.addError(e);
		});

		navigate('..', { state: { cancel: true } });
	};

	const getView = cond([
		[
			equals('cancelation_survey'),
			() => (
				<SurveyView
					cancelationReasons={cancelationReasons}
					getValues={getValues}
					pageContent={pageContent}
					register={register}
					setValue={setValue}
					setView={setView}
				/>
			),
		],
		[
			equals('additional_info'),
			() => (
				<AdditionalInfoView
					control={control}
					getValues={getValues}
					pageContent={pageContent}
					setValue={setValue}
					setView={setView}
				/>
			),
		],
	]);

	return <FullWidthForm onSubmit={handleSubmit(onSubmit)}>{getView(view)}</FullWidthForm>;
};

export default CancelationSurvey;
